// ---------------------------------------------------------------------------
// The two rows above the stage that do not change when a slide does.
//
// The strip is the round's context — the board, the target, or the conundrum —
// and the rail below it is every seated player in the server's reveal order.
// They are one file because they are one band of the screen: both are pinned
// above the stage for the whole reveal, both are sized against what a phone has
// left after the stage has taken its share, and a change to the height of
// either is a change to the other's budget.
//
// Neither of them shows an answer. That is what keeps them still while the
// stage moves, and it is why they are out of `frames.tsx`.
// ---------------------------------------------------------------------------

import { useEffect, useRef } from 'react';
import { TileRow } from '../../../components/round/atoms.js';
import { displayColorOf, playerById } from '../../../lib/playerLookup.js';
import { aggregateOf, conundrumIsRaced } from './aggregate.js';
import type { RevealView, Slide } from './view.js';

// --- The strip that never changes ------------------------------------------

/**
 * The board, the target, or the conundrum, on one line above everything.
 *
 * It is context rather than content: you cannot judge an eight-letter word
 * without the nine tiles it came out of, so this stays on screen for the whole
 * reveal at the smallest size that is still readable. It stands down for the
 * scores, which are the one slide that needs every pixel of the stage.
 */
export function BoardStrip({ view }: { view: RevealView }) {
  const { round, state } = view;

  if (round.type === 'numbers') {
    const tiles = round.numberTiles ?? [];
    return (
      <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 rounded-card border border-line bg-surface-2 px-3 py-2">
        <span className="label-mono">target</span>
        <span className="tabular text-lg font-extrabold">{round.target ?? '—'}</span>
        <span className="tabular ml-auto font-mono text-xs text-ink-dim">
          {tiles.length > 0 ? tiles.join(', ') : 'no tiles'}
        </span>
      </div>
    );
  }

  const letters =
    round.type === 'letters'
      ? (round.tiles ?? []).map((tile) => tile.letter.toUpperCase())
      : [...(round.scramble ?? '').toUpperCase()];

  const solver = playerById(state, round.solvedBy ?? null);

  // Everyone who took the risk, not just whoever was holding the buzzer when
  // the round ended. A failed buzz clears `buzzedBy`, so reading that field
  // alone announced "Nobody buzzed" about a round where four people had.
  const buzzes = new Set([
    ...(round.lockedOut ?? []),
    ...(round.buzzedBy === null || round.buzzedBy === undefined ? [] : [round.buzzedBy]),
    ...(round.solvedBy === null || round.solvedBy === undefined ? [] : [round.solvedBy]),
  ]).size;

  // **With no buzzer there is nothing to say about buzzing**, and saying
  // `Nobody buzzed` under a round anybody could answer would be the strip
  // reporting on a rule the room switched off. Everyone who has it right when
  // the clock stops scores — see `settings.conundrumBuzzer` — so the line is how
  // many got it, which is also the only shape that means anything in a room too
  // big to race.
  const solvers = Object.values(round.submissions).filter(
    (submission) => submission.valid === true,
  ).length;

  return (
    <div className="flex shrink-0 flex-col gap-1.5 rounded-card border border-line bg-surface-2 px-3 py-2">
      {/* Stacked on a phone. Nine inline tiles are 284px and the label is 72 of
          the 326 a phone has, so side by side the board wraps onto two rows and
          costs more height than the label ever saves. */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
        <span className="label-mono shrink-0">
          {round.type === 'letters' ? 'the board' : 'the conundrum'}
        </span>
        <div className="min-w-0 flex-1">
          <TileRow letters={letters} tone="board" size="inline" />
        </div>
      </div>
      {round.type === 'conundrum' ? (
        <p className="font-mono text-xs text-ink-dim">
          {/* What the scores are about to say is not this line's job. */}
          {!conundrumIsRaced(state)
            ? solvers === 0
              ? 'Nobody got it.'
              : `${solvers === 1 ? 'One player' : `${solvers} players`} got it.`
            : solver !== null
              ? `${solver.displayName} solved it.`
              : buzzes === 0
                ? 'Nobody buzzed.'
                : `${buzzes === 1 ? 'One buzz' : `${buzzes} buzzes`}, nobody solved it.`}
        </p>
      ) : null}
    </div>
  );
}

// --- The running order, which is also the way back -------------------------

/**
 * Two seats abreast is where the rail goes to two rows, and it is a threshold
 * rather than a preference.
 *
 * Below it a second row is half empty and costs the stage 40 pixels for nothing.
 * Above it the rail is the only thing on the screen that can be made to hold
 * more people without taking anything else away, because it scrolls: two rows
 * put twice as many faces either side of the one being revealed, which is the
 * whole of what the rail is read for during a long game.
 */
const RAIL_TWO_ROWS_FROM = 7;

/**
 * Every seated player, shortest declared first, with the one being revealed
 * marked and the viewer's own seat marked separately.
 *
 * It is also the navigation on a numbers round, and that is not a nicety. The
 * host's overrule lives beside the answer it is about, so without a way back to
 * an answer that has gone past, a ruling becomes unreachable the moment the next
 * player is called. Entries the server has not released are not selectable,
 * because the cursor is the whole contract.
 *
 * **It was one row of 44px pills and it is now two rows of 32.** Isaac, on a
 * phone in a long game: it could surely be compacted significantly to fit more
 * people, and there was a great deal of vertical padding in each tile. Both were
 * true — a 44px pill carrying 13px text is 18 pixels of nothing — and the fix is
 * the two of them together, because a shorter pill on its own only buys back
 * height the rail was not short of.
 *
 * The tap target goes with it, and that is the deliberate cost. A 32px pill is
 * under every target floor there is; the chevrons either side of the rail are
 * 44 and step through the same sequence, so the rail is the fast way rather than
 * the only way. On a letters round it is not a control at all — see `slideFor`.
 *
 * **The mark for `being revealed now` did nothing for the whole of a letters
 * round**, which is most of them. It was read off the current slide, and a
 * letters reveal has ONE slide: the leaderboard the answers land in. So nothing
 * on the rail ever became current, and the rail sat inert beside a panel that
 * was moving. The player being revealed there is the one whose answer landed
 * last, which is what `newest` already is on the panel itself.
 */
export function RunningOrder({
  view,
  slides,
  index,
  onSelect,
}: {
  view: RevealView;
  slides: Slide[];
  index: number;
  onSelect: (next: number) => void;
}) {
  const { round, ordered, declaredOf, isReleased, released, state } = view;
  const activeRef = useRef<HTMLLIElement | null>(null);

  const label =
    round.type === 'numbers'
      ? 'Declared values'
      : round.type === 'letters'
        ? 'Declared lengths'
        : 'Reveal order';

  const current = slides[index];
  const onSlide = current !== undefined && current.kind === 'answer' ? current.player.id : null;
  // A letters round has no slide per player, so the one being revealed is the
  // one whose answer landed last. Stepping back off the panel is not possible
  // there — it is a single slide — so there is no case where this disagrees with
  // what the viewer is looking at.
  const spotlitId = onSlide ?? (released[released.length - 1] ?? null);

  const twoRows = ordered.length >= RAIL_TWO_ROWS_FROM;

  // The rail is the only thing on this screen allowed to scroll, and it does it
  // sideways, keeping whoever is being revealed in the middle — so the pills
  // still to come are visibly to the right of it and a phone can see how much
  // of the round is left. jsdom has no scrollIntoView, hence the guard rather
  // than a `?.()`.
  useEffect(() => {
    const node = activeRef.current;
    if (node !== null && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest', inline: 'center' });
    }
  }, [spotlitId]);

  const slideFor = (playerId: string) =>
    slides.findIndex((slide) => slide.kind === 'answer' && slide.player.id === playerId);

  // **A room drawn as a distribution has no running order, and this is a rule
  // rather than a size cutoff.** The rail is one pill per seat: at five hundred
  // seats it is five hundred pills in a sideways scroller, which is not a
  // compressed version of the rail but the exact list the aggregated reveal
  // exists to stop drawing. It is also the navigation on a numbers round, and
  // there is nothing to navigate — the aggregated reveal has one body slide, and
  // the chevrons either side of this still step through it.
  //
  // Keyed on the aggregate being present rather than on a seat count, so the one
  // decision about whether this room is a room or an audience stays
  // `revealIsAggregated`'s. A host who forces `aggregateReveal: 'on'` at four
  // players gets the table and loses the rail, which is the same screen
  // everybody else in that room is looking at.
  //
  // **Below the hooks on purpose.** Every hook in this component has run by the
  // time this is reached; an early return above `useEffect` would be a
  // conditional hook, which is the one way this guard could be wrong.
  if (aggregateOf(round) !== null) return null;

  return (
    <ul
      aria-label={label}
      // Column-major over two rows, so the sequence still reads left to right
      // in pairs and the scroll axis stays horizontal. `grid-flow-col` with a
      // fixed row count is what makes a horizontally scrolling grid; a wrapping
      // flex row would wrap DOWN and then need vertical scrolling, which is the
      // one thing this screen does not do.
      className={`grid shrink-0 grid-flow-col justify-start gap-1 overflow-x-auto pb-0.5 [scrollbar-width:none] ${
        twoRows ? 'grid-rows-2' : 'grid-rows-1'
      }`}
    >
      {ordered.map((player) => {
        const declared = declaredOf(player.id);
        const reached = isReleased(player.id);
        const target = slideFor(player.id);
        const active = player.id === spotlitId;
        // **Yours, and only on your own screen.** Nothing about this is sent
        // anywhere: it is `state.youId` read locally, so the accent edge is on
        // your seat on your device and on nobody else's. A room of nine reading
        // the same rail is nine different rails, each with one pill outlined.
        const mine = player.id === state.youId;
        const color = displayColorOf(state, player);

        return (
          <li key={player.id} ref={active ? activeRef : undefined} className="shrink-0">
            <button
              type="button"
              disabled={target < 0}
              onClick={() => onSelect(target)}
              aria-current={active ? 'true' : undefined}
              // The border is on every pill, transparent until its turn, so a
              // pill becoming current cannot change the height of the rail. The
              // `you` edge is an inset shadow for the same reason — it draws
              // inside the box and cannot move a neighbour — and it composes
              // with the current-pill fill instead of arguing with it, which
              // matters because your own answer is very often the one landing.
              className={`flex h-8 min-w-[56px] items-center gap-1.5 rounded-chip border px-2 transition-colors duration-300 ease-out disabled:cursor-default ${
                active
                  ? 'border-blue-500/60 bg-blue-700/45'
                  : 'border-transparent bg-surface-2 enabled:hover:border-line'
              } ${mine ? 'shadow-[inset_0_0_0_2px_var(--c-accent)]' : ''} ${
                reached ? 'opacity-100' : 'opacity-55'
              }`}
            >
              {/* No colour, no swatch — and it is the whole rail or none of
                  it, because `colorForSeat` decides that once for the room.
                  A grey stand-in would be a colour that reads as an identity;
                  an empty circle in front of every name would be a colour
                  system left switched on with nothing in it. */}
              {color === null ? null : (
                <span
                  aria-hidden="true"
                  className="size-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: color }}
                />
              )}
              {/* **No cap on the name, and the cap that was here is what was
                  reported as "four characters".** It was `max-w-[7ch]`, and
                  `ch` is the advance width of a zero — about half an em in this
                  face — so seven of them is under four characters of a
                  proportional semibold, and `truncate` spends two more on the
                  ellipsis. Nothing was forcing the cap either: the rail scrolls
                  sideways and every pill is `shrink-0`, so a long name has never
                  been able to squeeze its neighbours. A display name is capped
                  at DISPLAY_NAME_MAX, which is 24 and about 150px here, so the
                  worst case is one wide pill in a strip that already scrolls. */}
              <span className="text-[11px] font-semibold whitespace-nowrap">
                {player.displayName}
              </span>
              {round.type === 'conundrum' ? null : (
                <span className="tabular ml-auto text-[13px] font-extrabold">
                  {declared === null ? '—' : declared}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
